/**
 * AI-powered domain picks — two modes:
 *   • Discover: user enters a niche + TLDs; GPT invents ~20 brandable domain
 *     candidates and we check availability against WHOIS cache / live lookup.
 *   • Rank:     paste a drop list (or auto-load the current Drops feed); GPT
 *     scores every domain by brandability + resale value and returns them
 *     sorted top-first.
 *
 * Each candidate row has a one-click Snipe button that opens the schedule form
 * with the WHOIS-predicted drop time pre-filled (or "now +5m" for available-now
 * regs from Discover).
 */
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Sparkles, Wand2, Crosshair, Loader2, ArrowUpDown, History, Send, Zap } from "lucide-react";
import {
  aiDiscoverDomains, aiRankDrops, fetchDropsFeed,
  aiHistory, aiHistoryDetail, aiDailyPicksRunNow, bulkSnipesFromPortfolio,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTimezone, formatInTz } from "@/lib/tz";

const DEFAULT_TLDS = [".com", ".io", ".co", ".ai", ".dev"];

function ScoreBadge({ score }) {
  const color =
    score >= 80 ? "bg-[#ECFDF5] text-[#047857]" :
    score >= 60 ? "bg-[#EFF6FF] text-[#2563EB]" :
    score >= 40 ? "bg-[#FEF3C7] text-[#92400E]" :
    "bg-[#F4F4F5] text-[#525252]";
  return (
    <span className={`inline-block font-mono text-[10px] tabular-nums px-1.5 py-0.5 rounded-sm ${color}`}>
      {score}
    </span>
  );
}

function CandidateRow({ c, mode, onSnipe }) {
  return (
    <tr
      className="border-b border-[#F4F4F5] hover:bg-[#FAFAFA]"
      data-testid={`ai-pick-row-${c.domain}`}
    >
      <td className="px-3 py-2 font-mono text-sm text-[#101828]">
        {c.domain}
      </td>
      <td className="px-3 py-2">
        <ScoreBadge score={c.quality_score || 0} />
      </td>
      <td className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-[#525252]">
        {c.category || "—"}
      </td>
      {mode === "discover" && (
        <td className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider">
          {c.available === true && <span className="text-[#047857]">Available</span>}
          {c.available === false && <span className="text-[#BE123C]">Taken</span>}
          {c.available == null && <span className="text-[#999]">Unknown</span>}
        </td>
      )}
      <td className="px-3 py-2 text-xs text-[#525252] max-w-[420px]">
        {c.reasoning || ""}
      </td>
      <td className="px-3 py-2 text-right">
        <button
          onClick={() => onSnipe(c)}
          disabled={mode === "discover" && c.available === false}
          data-testid={`ai-snipe-${c.domain}`}
          className="inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-1 border border-[#0A0A0A] text-[#101828] hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Crosshair className="w-3 h-3" strokeWidth={2} />
          Snipe
        </button>
      </td>
    </tr>
  );
}

export default function AIDomainPicks({ onScheduled }) {
  const { userTz } = useTimezone();
  const [mode, setMode] = useState("discover");
  const [niche, setNiche] = useState("");
  const [tlds, setTlds] = useState(DEFAULT_TLDS.slice(0, 3));
  const [count, setCount] = useState(20);
  const [rankInput, setRankInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [loadingDrops, setLoadingDrops] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [running, setRunning] = useState(false);
  const [bulking, setBulking] = useState(false);

  const loadHistory = async () => {
    try {
      const d = await aiHistory({ limit: 30 });
      setHistory(Array.isArray(d) ? d : []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadHistory(); }, []);

  const openHistoryRun = async (runId) => {
    try {
      const d = await aiHistoryDetail(runId);
      setMode(d.type === "discover" ? "discover" : "rank");
      setResults(d.results || []);
      setShowHistory(false);
      toast.success(`Loaded run · ${d.count} candidates`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load run");
    }
  };

  const triggerDailyNow = async () => {
    setRunning(true);
    try {
      const r = await aiDailyPicksRunNow();
      if (r.skipped) {
        toast.warning(`Daily picks skipped: ${r.skipped}`);
      } else if (r.email_ok) {
        toast.success(`Emailed top ${r.top10?.length || 0} picks`);
      } else {
        toast.error(`Ranked but email failed: ${r.email_msg}`);
      }
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Daily picks failed");
    } finally {
      setRunning(false);
    }
  };

  const runBulkAutoSchedule = async () => {
    if (!confirm(
      "Bulk auto-schedule: create a snipe for every portfolio-cached WHOIS " +
      "with a HIGH-confidence drop prediction inside the next 90 days. Continue?"
    )) return;
    setBulking(true);
    try {
      const r = await bulkSnipesFromPortfolio({
        days_ahead: 90,
        min_confidence: "high",
        offset_seconds: -5,
      });
      if (r.created_count === 0) {
        toast.info(`No new snipes to schedule (${r.skipped_count} skipped).`);
      } else {
        toast.success(`Scheduled ${r.created_count} snipe(s) · skipped ${r.skipped_count}`);
      }
      onScheduled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk schedule failed");
    } finally {
      setBulking(false);
    }
  };

  const toggleTld = (t) => {
    setTlds((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  };

  const runDiscover = async () => {
    if (!niche.trim()) {
      toast.warning("Enter a niche or theme first");
      return;
    }
    if (tlds.length === 0) {
      toast.warning("Pick at least one TLD");
      return;
    }
    setBusy(true);
    setResults([]);
    try {
      const r = await aiDiscoverDomains({
        niche: niche.trim(),
        tlds,
        count: Number(count) || 20,
        check_availability: true,
      });
      setResults(r.candidates || []);
      toast.success(`${r.count} candidates from GPT 5.6 Sol`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI discover failed");
    } finally {
      setBusy(false);
    }
  };

  const loadFromFeed = async () => {
    setLoadingDrops(true);
    try {
      const r = await fetchDropsFeed({ source: "sidn", limit: 60 });
      setRankInput((r.domains || []).join("\n"));
      toast.success(`Loaded ${r.domains?.length || 0} domains from SIDN drop list`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feed load failed");
    } finally {
      setLoadingDrops(false);
    }
  };

  const runRank = async () => {
    const domains = rankInput
      .split(/[\s,;]+/)
      .map((d) => d.trim().toLowerCase())
      .filter((d) => d.length >= 4 && d.includes("."));
    if (!domains.length) {
      toast.warning("Paste some drop list domains or load the feed");
      return;
    }
    setBusy(true);
    setResults([]);
    try {
      const r = await aiRankDrops({ domains: domains.slice(0, 60) });
      setResults(r.ranked || []);
      toast.success(`Ranked ${r.count} of ${domains.length} domains`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI rank failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSnipe = async (c) => {
    // Reuse the WHOIS-driven snipe endpoint; if the WHOIS cache doesn't have
    // a prediction yet (available-now discover pick), fall back to now+5min.
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/snipes/from-portfolio`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain: c.domain, registrar: "transip", offset_seconds: -5, refresh_whois: true }),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Snipe scheduled · ${c.domain} @ ${data.target_time?.slice(0, 16)}Z`);
        onScheduled?.();
        return;
      }
      const err = await r.json().catch(() => ({}));
      if (err?.detail?.reason === "no_whois_expiry" || err?.detail?.reason === "no_prediction") {
        // Fallback: manual schedule at now +5min
        const targetTime = new Date(Date.now() + 5 * 60 * 1000).toISOString();
        const r2 = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/snipes`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            domain: c.domain,
            registrar: "transip",
            target_time: targetTime,
            notes: `AI pick · score ${c.quality_score}`,
          }),
        });
        if (r2.ok) {
          toast.success(`Snipe scheduled · ${c.domain} @ now +5min (no WHOIS)`);
          onScheduled?.();
        } else {
          toast.error("Could not schedule fallback snipe");
        }
      } else {
        toast.error(err?.detail?.message || err?.detail || "Snipe failed");
      }
    } catch {
      toast.error("Snipe request failed");
    }
  };

  const sortedResults = useMemo(
    () => [...results].sort((a, b) => (b.quality_score || 0) - (a.quality_score || 0)),
    [results],
  );

  return (
    <div className="border border-[#eaecf0] p-5 bg-white" data-testid="ai-picks-panel">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="overline flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
            AI Domain Picks · GPT 5.6 Sol
          </div>
          <div className="font-display text-lg font-medium mt-0.5" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Let ChatGPT find good domains to snipe
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-start">
          <Button
            variant="outline"
            onClick={runBulkAutoSchedule}
            disabled={bulking}
            data-testid="bulk-auto-schedule"
            className="rounded-sm h-8 text-[10px] font-mono uppercase tracking-wider"
            title="Create snipes for every portfolio domain with a HIGH-confidence WHOIS drop prediction in the next 90 days"
          >
            {bulking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" strokeWidth={1.5} />}
            Bulk auto-schedule
          </Button>
          <Button
            variant="outline"
            onClick={triggerDailyNow}
            disabled={running}
            data-testid="run-daily-now"
            className="rounded-sm h-8 text-[10px] font-mono uppercase tracking-wider"
            title="Rank the SIDN feed now and email the top 10 to your notify address (normally runs at 08:00 local)"
          >
            {running ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" strokeWidth={1.5} />}
            Email top 10 now
          </Button>
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setShowHistory((s) => !s)}
              data-testid="history-toggle"
              className="rounded-sm h-8 text-[10px] font-mono uppercase tracking-wider"
            >
              <History className="w-3 h-3 mr-1" strokeWidth={1.5} />
              History ({history.length})
            </Button>
            {showHistory && (
              <div
                data-testid="history-dropdown"
                className="absolute right-0 mt-1 w-96 max-h-96 overflow-auto z-20 bg-white border border-[#eaecf0] shadow-lg rounded-sm"
              >
                {history.length === 0 && (
                  <div className="px-3 py-6 text-center text-xs font-mono text-[#999]">
                    No previous runs yet
                  </div>
                )}
                {history.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => openHistoryRun(h.id)}
                    data-testid={`history-run-${h.id}`}
                    className="w-full text-left px-3 py-2 border-b border-[#F4F4F5] hover:bg-[#FAFAFA] text-xs font-mono flex items-start gap-2"
                  >
                    <span className={`px-1.5 py-0.5 text-[9px] uppercase rounded-sm ${
                      h.type === "discover" ? "bg-[#EFF6FF] text-[#2563EB]" : "bg-[#ECFDF5] text-[#047857]"
                    }`}>{h.type}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[#101828] truncate">
                        {h.input?.niche || h.input?.source || `${h.count} domains`}
                      </div>
                      <div className="text-[10px] text-[#999]">
                        {formatInTz(h.created_at, userTz)} · {h.count} results
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex text-[10px] font-mono uppercase tracking-wider">
            <button
              onClick={() => { setMode("discover"); setResults([]); }}
              data-testid="mode-discover"
              className={`px-3 py-1.5 border transition-colors ${
                mode === "discover" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#eaecf0] text-[#525252] hover:bg-[#FAFAFA]"
              }`}
            >
              Discover
            </button>
            <button
              onClick={() => { setMode("rank"); setResults([]); }}
              data-testid="mode-rank"
              className={`px-3 py-1.5 border-t border-b border-r transition-colors ${
                mode === "rank" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#eaecf0] text-[#525252] hover:bg-[#FAFAFA]"
              }`}
            >
              Rank Drops
            </button>
          </div>
        </div>
      </div>

      {mode === "discover" && (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="overline">Niche or theme</Label>
            <Input
              value={niche}
              onChange={(e) => setNiche(e.target.value)}
              placeholder="e.g. luxury travel tech, AI copywriting, indie coffee brands"
              className="rounded-sm"
              data-testid="ai-niche-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="overline">TLDs</Label>
              <div className="flex flex-wrap gap-1">
                {DEFAULT_TLDS.map((t) => (
                  <button
                    key={t}
                    onClick={() => toggleTld(t)}
                    data-testid={`tld-toggle-${t}`}
                    className={`text-[10px] font-mono px-2 py-1 border rounded-sm transition-colors ${
                      tlds.includes(t)
                        ? "bg-[#03142f] text-white border-[#03142f]"
                        : "bg-white text-[#525252] border-[#eaecf0] hover:bg-[#FAFAFA]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="overline">Count</Label>
              <Input
                type="number"
                min={5}
                max={40}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                className="rounded-sm font-mono"
                data-testid="ai-count-input"
              />
            </div>
          </div>
          <Button
            onClick={runDiscover}
            disabled={busy}
            data-testid="run-discover"
            className="rounded-sm bg-[#0A0A0A] hover:bg-black text-white h-10 px-5"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            {busy ? "Thinking…" : "Discover candidates"}
          </Button>
        </div>
      )}

      {mode === "rank" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="overline">Domains to score (one per line, or comma / space separated · max 60)</Label>
            <Button
              variant="outline"
              onClick={loadFromFeed}
              disabled={loadingDrops}
              data-testid="load-feed"
              className="rounded-sm h-8 text-[10px] font-mono uppercase"
            >
              {loadingDrops ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ArrowUpDown className="w-3 h-3 mr-1" strokeWidth={1.5} />}
              Load from SIDN feed
            </Button>
          </div>
          <textarea
            value={rankInput}
            onChange={(e) => setRankInput(e.target.value)}
            placeholder={"foo.com\nbar.io\nbaz.dev"}
            data-testid="rank-input"
            rows={6}
            className="w-full rounded-sm border border-[#eaecf0] px-3 py-2 font-mono text-xs bg-white focus:outline-none focus:border-[#0A0A0A]"
          />
          <Button
            onClick={runRank}
            disabled={busy}
            data-testid="run-rank"
            className="rounded-sm bg-[#0A0A0A] hover:bg-black text-white h-10 px-5"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            {busy ? "Scoring…" : "Rank with GPT"}
          </Button>
        </div>
      )}

      {sortedResults.length > 0 && (
        <div className="mt-6 border border-[#eaecf0] overflow-auto max-h-[520px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#FAFAFA]">
              <tr className="border-b border-[#eaecf0]">
                <th className="text-left px-3 py-2 overline">Domain</th>
                <th className="text-left px-3 py-2 overline">Score</th>
                <th className="text-left px-3 py-2 overline">Category</th>
                {mode === "discover" && <th className="text-left px-3 py-2 overline">Availability</th>}
                <th className="text-left px-3 py-2 overline">AI reasoning</th>
                <th className="text-right px-3 py-2 overline">—</th>
              </tr>
            </thead>
            <tbody>
              {sortedResults.map((c) => (
                <CandidateRow key={c.domain} c={c} mode={mode} onSnipe={handleSnipe} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
